import type { DatePickerOptions } from "@capacitor-community/date-picker";
import { DatePicker } from "@capacitor-community/date-picker";
import { isDefined } from "@clipboard-health/util-ts";
import { useCallback, useRef, useState } from "react";

import { isCapacitorPlatform } from "../utils";

export interface UseNativeDatePickerOptions {
  /**
   * Text for the done button
   * @default "Done"
   */
  confirmText?: string;

  /**
   * Text for the cancel button
   * @default "Cancel"
   */
  cancelText?: string;

  /**
   * Timezone for the picker (e.g., "America/New_York")
   * If not provided, uses device's local timezone
   */
  timezone?: string;

  /**
   * Callback when user confirms/selects a date
   */
  onDateSelected?: (dateValue: string) => void;

  /**
   * Callback when user cancels the picker
   */
  onCancel?: () => void;
}

export interface UseNativeDatePickerResult {
  openDatePicker: (initialDate?: string) => Promise<string | undefined>;
  isLoading: boolean;
}

export function useNativeDatePicker(
  options: UseNativeDatePickerOptions = {}
): UseNativeDatePickerResult {
  const { onDateSelected, onCancel, confirmText, cancelText, timezone } = options;

  const [isLoading, setIsLoading] = useState(false);
  const inFlightRef = useRef(false);

  const openDatePicker = useCallback(
    async (initialDate?: string): Promise<string | undefined> => {
      if (inFlightRef.current || isLoading) {
        return undefined;
      }

      if (!isCapacitorPlatform()) {
        throw new Error(
          "useNativeDatePicker: Native date picker is only available on iOS and Android platforms. " +
            "This hook requires Capacitor native environment and cannot be used in web browsers."
        );
      }

      inFlightRef.current = true;
      setIsLoading(true);

      try {
        const pickerOptions: DatePickerOptions = {
          mode: "date",
          date: initialDate,
          doneText: confirmText,
          cancelText,
          is24h: false,
          timezone,
          ios: {
            style: "wheels",
            locale: "en-US",
          },
          android: {
            locale: "en-US",
          },
        };

        const result = await DatePicker.present(pickerOptions);

        if (isDefined(result?.value)) {
          onDateSelected?.(result.value);
          return result.value;
        }

        // User cancelled - no value returned
        onCancel?.();
        return undefined;
      } catch {
        return undefined;
      } finally {
        inFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [isLoading, confirmText, cancelText, timezone, onCancel, onDateSelected]
  );

  return {
    openDatePicker,
    isLoading,
  };
}
